import { useState } from 'react';

import { Box, Heading, Modal } from '@zillow/constellation-10';

import GetVerificationCode from './components/GetVerificationCode';
import VerificationCodeSent from './components/VerificationCodeSent';
import Finished from './components/Finished';

import { VERIFICATION_STEPS } from './constants';

import type { PhoneVerificationSource } from '@zg-rentals/ts-rental-manager-api-beta-client';

export type VerificationModalProps = {
  headerText: string;
  initialStep?: number;
  isContentEditable?: boolean;
  initialContentToVerify?: string;
  onVerificationConfirmation?: (contentValue: string) => Promise<void>;
  showVerificationModal: (value: boolean) => void;
  stepsConfig: Array<string>;
  verificationSuccessfulCTAText?: string;
  verificationSource: PhoneVerificationSource;
};

/**
 * Phone verification modal using beta web API (no Redux).
 * Used by listing-claiming contact verification flow.
 */
const VerificationModal = ({
  headerText,
  initialStep = 0,
  isContentEditable = true,
  initialContentToVerify = '',
  onVerificationConfirmation,
  showVerificationModal,
  stepsConfig,
  verificationSuccessfulCTAText = 'Return to listing',
  verificationSource,
}: VerificationModalProps) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(initialStep);
  const [verificationType, setVerificationType] = useState('');

  const incrementStep = (stepToJumpTo?: string) => {
    if (stepToJumpTo) {
      const stepToJumpToIndex = stepsConfig.findIndex((element) => element === stepToJumpTo);
      setCurrentStepIndex(stepToJumpToIndex >= 0 ? stepToJumpToIndex : currentStepIndex);
    } else {
      setCurrentStepIndex((i) => i + 1);
    }
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && showVerificationModal(false)}
      useDismissProps={{ outsidePress: false }}
      size={{ base: 'fullScreen', lg: 'md' }}
      header={<Heading level={2}>{headerText}</Heading>}
      body={
        <Box
          data-testid="VerificationModal"
          css={{
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {stepsConfig[currentStepIndex] === VERIFICATION_STEPS.GET_VERIFICATION_CODE && (
            <GetVerificationCode
              currentStepIndex={currentStepIndex}
              incrementStep={incrementStep}
              isContentEditable={isContentEditable}
              contentValue={initialContentToVerify}
              saveVerificationType={setVerificationType}
              verificationSource={verificationSource}
            />
          )}
          {stepsConfig[currentStepIndex] === VERIFICATION_STEPS.VERIFICATION_CODE_SENT && (
            <VerificationCodeSent
              currentStepIndex={currentStepIndex}
              incrementStep={incrementStep}
              isContentEditable={isContentEditable}
              contentValue={initialContentToVerify}
              verificationType={verificationType}
              onVerificationConfirmation={onVerificationConfirmation}
              verificationSource={verificationSource}
            />
          )}
          {stepsConfig[currentStepIndex] === VERIFICATION_STEPS.VERIFICATION_SUCCESS && (
            <Finished
              showVerificationModal={showVerificationModal}
              verificationSuccessfulCTAText={verificationSuccessfulCTAText}
              verificationMode="phone"
            />
          )}
        </Box>
      }
    />
  );
};

export { VerificationModal };
